"use client";

import { useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  INPUT,
  INPUT_MONO,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { DecisionResults } from "@/components/tools/math/decision-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

/** English strings. A translated content file overrides these through `ui`. */
export const DECISION_MATRIX_CALCULATOR_STRINGS = {
  weightMode: "Weights entered as",
  weightPoints: "Points (any positive numbers)",
  weightPercent: "Percentages (totaling 100)",
  scale: "Score scale",
  scale5: "1 to 5",
  scale10: "1 to 10",
  matrixHeading: "Your matrix",
  matrixHint:
    "Each column is a criterion with its weight; each row is an option. Score every option on every criterion, where a higher score is better.",
  optionColumn: "Option",
  weightShort: "Weight",
  criterionName: "Name of criterion {n}",
  weightLabel: "Weight of {criterion}",
  optionName: "Name of option {n}",
  scoreLabel: "Score for {option} on {criterion}",
  removeCriterion: "Remove criterion {criterion}",
  removeOption: "Remove option {option}",
  addCriterion: "Add criterion",
  addOption: "Add option",
  newCriterion: "Criterion {n}",
  newOption: "Option {n}",
  exampleCriteria: [
    "Feasibility",
    "Personal interest",
    "Access to data",
    "Career value",
  ],
  exampleOptions: [
    "Survey study",
    "Interview study",
    "Secondary data analysis",
  ],
  percentMismatch:
    "The percentages add up to {total}%, not 100%, so they were scaled to 100% — the ranking is unaffected.",
  weightZero: "Give at least one criterion a weight greater than 0.",
  badWeight: "Weights must be numbers of 0 or more. Check: {criteria}.",
  missingScores:
    "one: {count} score is still empty|other: {count} scores are still empty",
  incomplete:
    "Score every option on every criterion to see the results ({missing}).",
  outOfRange: "Scores must be numbers from 1 to {max}. Check: {cells}.",
  cell: "{option} × {criterion}",
  results: "Results",
  winner: "Top option",
  margin: "Lead over second place",
  stability: "±20% weight check",
  stable: "Stable",
  sensitive: "Sensitive",
  notApplicable: "—",
  rankColumn: "Rank",
  scoreColumn: "Weighted score (out of {max})",
  percentColumn: "Share of maximum",
  marginNote:
    "{winner} leads {runnerUp} by {margin} points, {share}% of the maximum score.",
  closeCall:
    "That lead is under 5% of the maximum score, so a small change to one score or weight could reverse it. Treat it as a near-tie and look again at the evidence behind the scores.",
  tieNote:
    "{options} tie for first place. Revisit the scores, or add a criterion that separates them.",
  listJoin: ", ",
  workingHeading: "How the top score was calculated",
  sensitivityHeading: "Sensitivity check",
  sensitivityIntro:
    "Each weight in turn is moved 20% down and 20% up with the others unchanged, and every option is re-scored. The last column shows how far that one weight would have to move before another option draws level.",
  criterionColumn: "Criterion",
  weightColumn: "Weight",
  downColumn: "Winner at −20%",
  upColumn: "Winner at +20%",
  tippingColumn: "Change that ties the lead",
  noFlip: "No change does",
  tippingUp: "+{percent}% ({option})",
  tippingDown: "−{percent}% ({option})",
  stableSummary:
    "The winner holds: no single ±20% change in a weight alters the top option.",
  sensitiveSummary:
    "The winner depends on these weights: {criteria}. Check that they reflect what really matters before you rely on the result.",
  tieNoCheck:
    "The sensitivity check needs a single leader, so it is skipped while options are tied.",
} as const;

export type DecisionStrings = typeof DECISION_MATRIX_CALCULATOR_STRINGS;

interface Criterion {
  id: number;
  name: string;
  weight: string;
}

interface OptionRow {
  id: number;
  name: string;
  scores: Record<number, string>;
}

const EXAMPLE_WEIGHTS = ["40", "25", "20", "15"];
const EXAMPLE_SCORES = [
  ["4", "3", "3", "3"],
  ["3", "5", "3", "4"],
  ["5", "2", "5", "3"],
];
const MAX_CRITERIA = 8;
const MAX_OPTIONS = 8;
const FIRST_FREE_ID = 100;

function exampleCriteria(names: readonly string[]): Criterion[] {
  return names.map((name, i) => ({
    id: i + 1,
    name,
    weight: EXAMPLE_WEIGHTS[i] ?? "1",
  }));
}

function exampleOptions(names: readonly string[]): OptionRow[] {
  return names.map((name, i) => ({
    id: 11 + i,
    name,
    scores: Object.fromEntries(
      (EXAMPLE_SCORES[i] ?? []).map((score, j) => [j + 1, score])
    ),
  }));
}

/** Read the matrix, or say exactly what is missing or out of range. */
function readMatrix(criteria: Criterion[], options: OptionRow[], max: number) {
  const weights = criteria.map((c) => parseNumberInput(c.weight));
  const badWeights = criteria.filter(
    (_, j) => weights[j] === null || (weights[j] ?? 0) < 0
  );
  const scores = options.map((o) =>
    criteria.map((c) => parseNumberInput(o.scores[c.id] ?? ""))
  );
  const missing = scores.flat().filter((s) => s === null).length;
  const outOfRange = options.flatMap((o, i) =>
    criteria.flatMap((c, j) => {
      const s = scores[i][j];
      return s !== null && (s < 1 || s > max)
        ? [{ option: o.name, criterion: c.name }]
        : [];
    })
  );
  return { weights, badWeights, scores, missing, outOfRange };
}

export default function DecisionMatrixCalculatorWidget() {
  const strings = useToolUi(DECISION_MATRIX_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [weightMode, setWeightMode] = useState<"points" | "percent">("percent");
  const [scale, setScale] = useState<"5" | "10">("5");
  const [criteria, setCriteria] = useState(() =>
    exampleCriteria(strings.exampleCriteria)
  );
  const [options, setOptions] = useState(() =>
    exampleOptions(strings.exampleOptions)
  );
  const nextId = useRef(FIRST_FREE_ID);
  const max = Number(scale);
  const fresh = () => {
    nextId.current += 1;
    return nextId.current;
  };

  const updateCriterion = (id: number, patch: Partial<Criterion>) =>
    setCriteria((list) =>
      list.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
  const updateOption = (id: number, patch: Partial<OptionRow>) =>
    setOptions((list) =>
      list.map((o) => (o.id === id ? { ...o, ...patch } : o))
    );
  const setScore = (option: OptionRow, criterionId: number, value: string) =>
    updateOption(option.id, {
      scores: { ...option.scores, [criterionId]: value },
    });

  const matrix = readMatrix(criteria, options, max);
  const weightSum = matrix.weights.reduce<number>(
    (sum, w) => sum + (w ?? 0),
    0
  );
  const label = (name: string, fallback: string) =>
    name.trim() === "" ? fallback : name;

  let problem: string | null = null;
  if (matrix.badWeights.length > 0) {
    problem = t(strings.badWeight, {
      criteria: matrix.badWeights.map((c) => c.name).join(strings.listJoin),
    });
  } else if (!(weightSum > 0)) {
    problem = strings.weightZero;
  } else if (matrix.outOfRange.length > 0) {
    problem = t(strings.outOfRange, {
      max: fmt(max),
      cells: matrix.outOfRange
        .map((cell) => t(strings.cell, cell))
        .join(strings.listJoin),
    });
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="dm-weight-mode"
          label={strings.weightMode}
          onChange={setWeightMode}
          options={[
            { value: "percent", label: strings.weightPercent },
            { value: "points", label: strings.weightPoints },
          ]}
          value={weightMode}
        />
        <SelectField
          id="dm-scale"
          label={strings.scale}
          onChange={setScale}
          options={[
            { value: "5", label: strings.scale5 },
            { value: "10", label: strings.scale10 },
          ]}
          value={scale}
        />
      </div>
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.matrixHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.matrixHint}</p>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className={TABLE}>
            <thead>
              <tr>
                <th className={TH} scope="col">
                  {strings.optionColumn}
                </th>
                {criteria.map((c, j) => {
                  const name = label(
                    c.name,
                    t(strings.newCriterion, { n: j + 1 })
                  );
                  return (
                    <th
                      className={`${TH} min-w-[8rem] align-top normal-case`}
                      key={c.id}
                      scope="col"
                    >
                      <label className="sr-only" htmlFor={`dm-c-${c.id}`}>
                        {t(strings.criterionName, { n: j + 1 })}
                      </label>
                      <input
                        className={INPUT}
                        id={`dm-c-${c.id}`}
                        onChange={(e) =>
                          updateCriterion(c.id, { name: e.target.value })
                        }
                        value={c.name}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <label
                          className="font-normal text-muted-foreground text-xs"
                          htmlFor={`dm-w-${c.id}`}
                        >
                          {strings.weightShort}
                        </label>
                        <input
                          aria-label={t(strings.weightLabel, {
                            criterion: name,
                          })}
                          className={`${INPUT_MONO} w-20`}
                          id={`dm-w-${c.id}`}
                          inputMode="decimal"
                          onChange={(e) =>
                            updateCriterion(c.id, { weight: e.target.value })
                          }
                          value={c.weight}
                        />
                        <button
                          aria-label={t(strings.removeCriterion, {
                            criterion: name,
                          })}
                          className={BUTTON_SMALL}
                          disabled={criteria.length <= 1}
                          onClick={() =>
                            setCriteria((list) =>
                              list.filter((x) => x.id !== c.id)
                            )
                          }
                          type="button"
                        >
                          ×
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {options.map((o, i) => {
                const name = label(o.name, t(strings.newOption, { n: i + 1 }));
                return (
                  <tr key={o.id}>
                    <th
                      className={`${TD} min-w-[10rem] font-normal`}
                      scope="row"
                    >
                      <div className="flex items-center gap-2">
                        <label className="sr-only" htmlFor={`dm-o-${o.id}`}>
                          {t(strings.optionName, { n: i + 1 })}
                        </label>
                        <input
                          className={INPUT}
                          id={`dm-o-${o.id}`}
                          onChange={(e) =>
                            updateOption(o.id, { name: e.target.value })
                          }
                          value={o.name}
                        />
                        <button
                          aria-label={t(strings.removeOption, { option: name })}
                          className={BUTTON_SMALL}
                          disabled={options.length <= 2}
                          onClick={() =>
                            setOptions((list) =>
                              list.filter((x) => x.id !== o.id)
                            )
                          }
                          type="button"
                        >
                          ×
                        </button>
                      </div>
                    </th>
                    {criteria.map((c, j) => (
                      <td className={TD} key={c.id}>
                        <label
                          className="sr-only"
                          htmlFor={`dm-s-${o.id}-${c.id}`}
                        >
                          {t(strings.scoreLabel, {
                            option: name,
                            criterion: label(
                              c.name,
                              t(strings.newCriterion, { n: j + 1 })
                            ),
                          })}
                        </label>
                        <input
                          className={`${INPUT_MONO} w-20`}
                          id={`dm-s-${o.id}-${c.id}`}
                          inputMode="decimal"
                          onChange={(e) => setScore(o, c.id, e.target.value)}
                          value={o.scores[c.id] ?? ""}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={criteria.length >= MAX_CRITERIA}
            onClick={() => {
              const id = fresh();
              setCriteria((list) => [
                ...list,
                {
                  id,
                  name: t(strings.newCriterion, { n: list.length + 1 }),
                  weight: "1",
                },
              ]);
            }}
            type="button"
          >
            {strings.addCriterion}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={options.length >= MAX_OPTIONS}
            onClick={() => {
              const id = fresh();
              setOptions((list) => [
                ...list,
                {
                  id,
                  name: t(strings.newOption, { n: list.length + 1 }),
                  scores: {},
                },
              ]);
            }}
            type="button"
          >
            {strings.addOption}
          </button>
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {!problem && matrix.missing > 0 ? (
          <Notice>
            {t(strings.incomplete, {
              missing: tPlural(strings.missingScores, locale, matrix.missing, {
                count: fmt(matrix.missing),
              }).trim(),
            })}
          </Notice>
        ) : null}
        {!problem && matrix.missing === 0 ? (
          <DecisionResults
            criteria={criteria.map((c, j) =>
              label(c.name, t(strings.newCriterion, { n: j + 1 }))
            )}
            max={max}
            options={options.map((o, i) =>
              label(o.name, t(strings.newOption, { n: i + 1 }))
            )}
            percentMode={weightMode === "percent"}
            scores={matrix.scores as number[][]}
            strings={strings}
            weights={matrix.weights as number[]}
          />
        ) : null}
      </section>
    </>
  );
}
